import { useCallback, useState, useEffect, useMemo } from 'react';
import { List, fromJS, Map as ImmutableMap } from 'immutable';
import { DESC, ASC } from 'reporting-data/constants/sortOrder';
import { TIME_SERIES, FUNNEL, PIPELINE } from 'reporting-data/constants/configTypes';
import { ALPHA, METRIC } from 'reporting-data/constants/sortType';
import { isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { isConfigReport, isUnifiedEventsQLReport } from 'reporting-data/tsTypes/reportTypes';
import { toMetricKey, fromMetricKey, isMetricWithType } from 'reporting-data/v2/dataset/datasetMetrics';
import { getConfigType, getDisplayParamsColumns, getMetrics, getDimensions, getSort } from 'reporting-data/report/configReportGetters';
import { ReportingLogger } from 'reporting-data/monitoring/reportingLogger';
const SORT_PATH = ['config', 'sort'];
export const ONSORT_PATH = ['displayParams', 'onSort'];
const SO_SORT_PAGE_ACTION = 'SingleObjectInlineTableSort';
const unsortableProperties = ['associations', 'associations.contact', 'associations.deal', 'associations.company', 'engagement.details', 'dealstage.probability', 'senderScore'];
const sortableUnifiedEventsQL = ['timestamp'];
function addSortHandlers(report, onSort) {
  const configType = getConfigType(report);
  const dimensions = getDimensions(report);
  const sortColumns = getDisplayParamsColumns(report);
  const hasDefinedSortColumns = sortColumns === null || sortColumns === void 0 ? void 0 : sortColumns.some(column => column.has('onSort'));
  if (configType && [TIME_SERIES, FUNNEL, PIPELINE].includes(configType)) {
    return report;
  }
  if (dimensions.equals(List(['people'])) || hasDefinedSortColumns) {
    return report;
  }
  const properties = getMetrics(report)
  // for UnifiedEventQL, currently it only support sort on a timestampe field. for the rest of the fields, we should disable the sorting
  .filter(isUnifiedEventsQLReport(report) ? metric => sortableUnifiedEventsQL.includes(metric.get('property')) : metric => !unsortableProperties.includes(metric.get('property'))).flatMap(metric => {
    const metricTypes = metric.get('metricTypes');
    if (!metricTypes || metricTypes.isEmpty()) {
      return List([metric.get('property')]);
    }
    return metricTypes.map(type => toMetricKey({
      property: metric.get('property'),
      type
    }));
  });
  return report.updateIn(['displayParams', 'columns'], ImmutableMap(), oldColumns => properties.reduce((acc, column) => acc.setIn([column, 'onSort'], onSort), oldColumns));
}
export function useSortableReport(asyncReport, allowSort, onReportChange) {
  const [sort, setSort] = useState(undefined);
  const [previousReportSort, setPreviousReportSort] = useState();
  useEffect(() => {
    if (!allowSort || !isSucceeded(asyncReport)) {
      return;
    }
    const report = asyncReport.data;
    if (isConfigReport(report)) {
      const reportSort = getSort(report);
      if (reportSort && (!previousReportSort || !reportSort.equals(previousReportSort))) {
        setPreviousReportSort(reportSort);
        setSort(reportSort);
      }
    }
  }, [asyncReport, allowSort, setSort, previousReportSort, setPreviousReportSort]);
  const onSort = useCallback((field, {
    target: {
      value
    }
  }) => {
    if (!isSucceeded(asyncReport)) {
      return;
    }
    const {
      property,
      type: metricType
    } = fromMetricKey(field);
    const report = asyncReport.data;
    const dimensions = getDimensions(report);
    const reportingLogger = new ReportingLogger();
    reportingLogger.addReportAttributes(asyncReport.data);
    reportingLogger.addAttributes({
      property,
      sortOrder: value
    });
    reportingLogger.sendPageAction(SO_SORT_PAGE_ACTION);
    const newSort = fromJS([{
      property,
      order: value === 'descending' ? DESC : ASC,
      type: dimensions.includes(property) ? ALPHA : METRIC,
      metricType: isMetricWithType(field) ? metricType : undefined
    }]);
    if (!newSort.equals(sort)) {
      setSort(newSort);
      if (onReportChange) {
        onReportChange(asyncReport.data.setIn(SORT_PATH, newSort));
      }
    }
  }, [asyncReport, onReportChange, sort]);
  const reportWithSort = useMemo(() => {
    if (!allowSort || !isSucceeded(asyncReport)) {
      return asyncReport;
    }
    const report = asyncReport.data;
    if (!isConfigReport(report)) {
      // This sorting applies to ElasticSearch based reports
      // SF reports handle their own sorting
      return asyncReport;
    }
    if (report.hasIn(ONSORT_PATH)) {
      return asyncReport;
    }
    const reportWithSortHandler = addSortHandlers(report, onSort);
    const reportSort = getSort(report);
    const hasSortChanged = sort && (!reportSort || !reportSort.equals(sort));
    const sortedReport = hasSortChanged ? reportWithSortHandler.setIn(SORT_PATH, sort) : reportWithSortHandler;
    return Object.assign({}, asyncReport, {
      data: sortedReport
    });
  }, [allowSort, asyncReport, onSort, sort]);
  return reportWithSort;
}
export const __TESTABLE__ = {
  unsortableProperties
};